import {ErrorHandler, inject} from "@angular/core";
import {DOCUMENT} from "@angular/common";
import {NotificationService} from "../services/notification/notification.service";
import {isDefined, nonBlank} from "../libs/utils/epic.utils";
import {WrapperDto} from "../repositories/dto/wrapper.dto";
import {ExceptionErrorDto} from "../repositories/dto/exception-error.dto";
import {LoggingService} from "../services/logger/logging.service";
import {HttpErrorResponse} from "@angular/common/http";

export class GlobalErrorHandler implements ErrorHandler {

  private static readonly MAX_TEXT_LENGTH: number = 500;

  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly document: Document = inject(DOCUMENT);

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  handleError(error: any): void {
    if (isDefined(error)) {
      if (error instanceof Error) {
        // information too technical for the user
        // this.notificationService.addErrorMessage(error.message);
        // Context (page + error name) survives minification and makes the one-line log identifiable, even without the stack.
        this.log.error(this.buildMessage(error.message, error.name), [], undefined, "Error", error.stack);
      } else if (error instanceof HttpErrorResponse) {
        this.log.error(this.buildMessage(error.message), [], undefined, "HttpErrorResponse", undefined, error.status);
      } else if (error.error && error.error.dataType && error.error.dataType === 'ExceptionErrorDto') {
        new WrapperDto<ExceptionErrorDto>(error.error).getErrors()
          .filter(err => nonBlank(err.message))
          .forEach((err) => this.notificationService.addErrorMessage(err.message!));
      } else {
        console.warn("Unhandled error", error);
        const type: string | undefined = this.getType(error);
        this.log.error(this.buildMessage(this.buildErrorText(error), type), [], undefined, type);
      }
    }
  }

  // Plain objects (e.g., OAuthErrorEvent) stringify to "[object]" - serialize them instead so the one-line log stays diagnosable.
  private buildErrorText(error: any): string {
    if (typeof error === 'object') {
      try {
        const seen: WeakSet<object> = new WeakSet<object>();
        const json: string | undefined = JSON.stringify(error, (_key: string, value: any) => {
          if (isDefined(value) && typeof value === 'object') {
            if (seen.has(value)) {
              return '[circular]';
            }
            seen.add(value);
          }
          return value;
        });
        if (nonBlank(json) && json !== '{}') {
          return json!.length > GlobalErrorHandler.MAX_TEXT_LENGTH ? json!.substring(0, GlobalErrorHandler.MAX_TEXT_LENGTH) + '...' : json!;
        }
      } catch {
        // not serializable (e.g., BigInt) - fall back to String() below
      }
    }
    return String(error);
  }

  // Plain objects carry no 'name' - fall back to the constructor name so the type is not reported as 'unknown'.
  private getType(error: any): string | undefined {
    if (nonBlank(error.name)) {
      return error.name;
    }
    const constructor: string | undefined = error?.constructor?.name;
    return nonBlank(constructor) && constructor !== 'Object' ? constructor : undefined;
  }

  // Enrich the raw message with page context so it stays identifiable in minified prod builds (stack has no source names there).
  private buildMessage(message: string, name?: string): string {
    const page: string = this.getPage();
    const parts: string[] = [message];
    if (nonBlank(name) && !message.includes(name!)) {
      parts.push(`name=${name}`);
    }
    if (nonBlank(page)) {
      parts.push(`page=${page}`);
    }
    return parts.join(" | ");
  }

  private getPage(): string {
    const location: Location = this.document.location;
    if (isDefined(location)) {
      return location.pathname + location.search + location.hash;
    }
    return "";
  }
}
